import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import MainLayout from "@/components/layout/MainLayout";
import AgentDetailSkeleton from "@/components/agent/detail/AgentDetailSkeleton";
import AgentBehaviorTab from "@/components/agent/tabs/AgentBehaviorTab";
import AgentSplashPageTab from "@/components/agent/tabs/AgentSplashPageTab";
import AgentPersonalityTab from "@/components/agent/tabs/AgentPersonalityTab";
import AgentPostChatTab from "@/components/agent/tabs/AgentPostChatTab";
import AgentFormFieldsTab from "@/components/agent/tabs/AgentFormFieldsTab";
import AgentPublishTab from "@/components/agent/tabs/AgentPublishTab";
import AgentAdminTab from "@/components/agent/tabs/AgentAdminTab";
import AgentPublishModal from "@/components/agent/modals/AgentPublishModal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AgentStats from "@/components/agent/detail/AgentStats";
import { useAgentConversations } from "@/hooks/chat/useAgentConversations";
import { useInternalConversations } from "@/hooks/chat/useInternalConversations";
import { clearAllCaches } from "@/services/templateService";
import { trackActiveAgents } from "@/services/safeUsageTracking";
import {
  X,
  MessageCircle,
  Info,
  Edit,
  Check,
  Upload,
  Image,
  Settings,
  FormInput,
  Zap,
  BookOpen,
  Shield,
  Share,
  User,
  Mail,
  Heart,
  Plus,
  BellRing,
  Copy,
  ExternalLink,
  BarChart3,
  ArrowLeft,
  AlignJustify,
} from "lucide-react";
import AgentContextMenu from "@/components/agent/detail/AgentContextMenu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ImageLibraryPicker from "@/components/common/ImageLibraryPicker";
import AgentSkillsTab from "@/components/agent/tabs/AgentSkillsTab";
import AgentKnowledgeTab from "@/components/agent/tabs/AgentKnowledgeTab";
import AgentInternalChatTab from "@/components/agent/tabs/AgentInternalChatTab";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from "date-fns";
import AgentTrackTab from "@/components/agent/tabs/AgentTrackTab";

/* ------------------------------------------------------------------ */
/* --------------------------- helper UI ---------------------------- */
/* ------------------------------------------------------------------ */

const FieldsTabs = ({
  activeFieldTab,
  onTabChange,
}: {
  activeFieldTab: string;
  onTabChange: (tab: string) => void;
}) => (
  <div className="mb-8">
    <TabsList>
      <TabsTrigger
        value="fields-in-chat"
        onClick={() => onTabChange("fields-in-chat")}
        className={
          activeFieldTab === "fields-in-chat" ? "data-[state=active]" : ""
        }
      >
        During the chat
      </TabsTrigger>
      <TabsTrigger
        value="fields-post-chat"
        onClick={() => onTabChange("fields-post-chat")}
        className={
          activeFieldTab === "fields-post-chat" ? "data-[state=active]" : ""
        }
      >
        Extracted post chat
      </TabsTrigger>
    </TabsList>
  </div>
);

/* ------------------------------------------------------------------ */
/* --------------------------- main page ---------------------------- */
/* ------------------------------------------------------------------ */

const AgentDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [agent, setAgent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get current tabs from URL params with defaults
  const currentTab = searchParams.get("tab") || "build";
  const currentBuildTab = searchParams.get("buildTab") || "behavior";
  const currentFieldTab = searchParams.get("fieldTab") || "fields-in-chat";

  const [activeTab, setActiveTab] = useState<string>(currentTab);
  const [activeBuildTab, setActiveBuildTab] = useState<string>(currentBuildTab);
  const [activeFieldTab, setActiveFieldTab] = useState<string>(currentFieldTab);
  const { organization, isAdmin, user } = useAuth();
  const { toast } = useToast();

  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const widgetScriptRef = useRef<HTMLScriptElement | null>(null);
  const { conversations, isLoading: statsLoading } = useAgentConversations(
    id || "",
  );
  const {
    conversations: internalConversations,
    isLoading: internalConversationsLoading,
    refetch: refetchInternalConversations,
  } = useInternalConversations(id || "");

  // Agent editing states
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState(agent?.title || "");
  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState(agent?.agent_name || "");
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Conversation management states for chat tab
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [isCreatingNewConversation, setIsCreatingNewConversation] =
    useState(false);

  // Publish modal state
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

  // Sync state with URL changes
  useEffect(() => {
    setActiveTab(currentTab);
  }, [currentTab]);

  useEffect(() => {
    setActiveBuildTab(currentBuildTab);
  }, [currentBuildTab]);

  useEffect(() => {
    setActiveFieldTab(currentFieldTab);
  }, [currentFieldTab]);

  // Auto-select first conversation when switching to chat tab, but don't override manual selections
  useEffect(() => {
    console.log("Auto-selection useEffect triggered:", {
      activeTab,
      internalConversationsLoading,
      conversationsLength: internalConversations.length,
      selectedConversationId,
    });

    if (
      activeTab === "chat" &&
      !internalConversationsLoading &&
      internalConversations.length > 0
    ) {
      // Only auto-select if no conversation is selected AND we don't have a valid selected conversation
      const selectedConversationExists =
        selectedConversationId &&
        internalConversations.some(
          (conv) => conv.id === selectedConversationId,
        );

      if (!selectedConversationId || !selectedConversationExists) {
        console.log(
          "Auto-selecting first conversation:",
          internalConversations[0].id,
        );
        setSelectedConversationId(internalConversations[0].id);
      }
    } else if (activeTab !== "chat") {
      // Reset selected conversation when leaving chat tab
      console.log("Resetting conversation selection - leaving chat tab");
      setSelectedConversationId(null);
    }
  }, [activeTab, internalConversations, internalConversationsLoading]);

  // Debug logging for conversation ID changes
  useEffect(() => {
    console.log("selectedConversationId changed to:", selectedConversationId);
  }, [selectedConversationId]);

  // Calculate stats
  const stats = useMemo(() => {
    if (statsLoading || !conversations) {
      return { total: 0, last30Days: 0 };
    }

    const nonAnonymousConversations = conversations.filter(
      (conversation) =>
        conversation.agent_chat_user_id &&
        conversation.agent_chat_user_id.trim() !== "",
    );

    const totalConversations = nonAnonymousConversations.length;

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentConversations = nonAnonymousConversations.filter(
      (conversation) => {
        const createdAt = new Date(conversation.created_at);
        return createdAt >= thirtyDaysAgo;
      },
    ).length;

    return { total: totalConversations, last30Days: recentConversations };
  }, [conversations, statsLoading]);

  /* ----------------------- fetch agent data ----------------------- */

  useEffect(() => {
    if (!id) return;
    const fetchAgent = async () => {
      try {
        const { data, error } = await supabase
          .from("agents")
          .select("*")
          .eq("id", id)
          .single();
        if (error) throw error;
        setAgent(data);
        setTitle(data.title || "");
        setName(data.agent_name || "");
      } catch (err: any) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAgent();
  }, [id]);

  /* -------------------------- update agent ------------------------- */

  const updateAgent = async (data: Partial<any>): Promise<boolean> => {
    if (!id) return false;
    try {
      const hasChanges = Object.entries(data).some(([k, v]) => agent[k] !== v);
      if (!hasChanges) return true;

      const { error } = await supabase.from("agents").update(data).eq("id", id);
      if (error) throw error;

      setAgent((prev: any) => ({ ...prev, ...data }));
      clearAllCaches();

      // Track active agents count if is_active field changed
      if ("is_active" in data && agent?.organization_id) {
        trackActiveAgents(agent.organization_id).catch((error) =>
          console.warn("Failed to track active agents:", error),
        );
      }

      toast({ title: "Success", description: "Agent updated successfully" });
      return true;
    } catch (err: any) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Update failed",
        description: err.message || "Failed to update agent",
      });
      return false;
    }
  };

  /* ---------------------- widget loading ---------------------- */

  useEffect(() => {
    // Load widget script when not on chat tab and not on mobile
    if (activeTab !== "chat" && !isMobile && agent) {
      // Clean up any existing script first
      if (widgetScriptRef.current && widgetScriptRef.current.parentNode) {
        widgetScriptRef.current.parentNode.removeChild(widgetScriptRef.current);
      }

      // Create and load the embed script
      const script = document.createElement("script");
      script.src = "/embed.js";
      script.setAttribute("data-agent-id", agent.public_id);
      script.setAttribute("data-type", "bubble");
      script.setAttribute(
        "data-welcome-message",
        agent.welcome_message || "Hi! How can I assist you?",
      );

      document.body.appendChild(script);
      widgetScriptRef.current = script;
    } else {
      // Clean up widget when switching to chat tab or on mobile
      if (widgetScriptRef.current && widgetScriptRef.current.parentNode) {
        widgetScriptRef.current.parentNode.removeChild(widgetScriptRef.current);
        widgetScriptRef.current = null;
      }

      // Also remove any widget containers that might have been created
      const containers = document.querySelectorAll(
        ".hubflo-widget, .ai-chat-widget-container",
      );
      containers.forEach((container) => {
        if (container && container.parentNode) {
          container.parentNode.removeChild(container);
        }
      });
    }

    // Cleanup on unmount
    return () => {
      if (widgetScriptRef.current && widgetScriptRef.current.parentNode) {
        widgetScriptRef.current.parentNode.removeChild(widgetScriptRef.current);
      }
      const containers = document.querySelectorAll(
        ".hubflo-widget, .ai-chat-widget-container",
      );
      containers.forEach((container) => {
        if (container && container.parentNode) {
          container.parentNode.removeChild(container);
        }
      });
    };
  }, [activeTab, isMobile, agent?.public_id, agent?.welcome_message]);

  /* ------------------------ early returns ------------------------- */

  if (loading) return <AgentDetailSkeleton />;

  if (error || !agent) {
    return (
      <MainLayout>
        <div className="px-8 py-8 w-full">
          <div className="text-center p-8">
            <h2 className="text-2xl font-bold mb-4">Error</h2>
            <p className="text-muted-foreground mb-6">
              {error || "Agent not found"}
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  /* ----------------------- tab change logic ----------------------- */

  // Helper function to update URL params
  const updateUrlParams = (updates: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      newParams.set(key, value);
    });
    setSearchParams(newParams);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    updateUrlParams({ tab });
  };

  const handleBuildTabChange = (tab: string) => {
    // If someone tries to navigate to the publish tab (which no longer exists), redirect to behavior
    if (tab === "publish") {
      setActiveBuildTab("behavior");
      updateUrlParams({ buildTab: "behavior" });
      return;
    }

    if (tab === "form") {
      setActiveBuildTab("fields-in-chat");
      setActiveFieldTab("fields-in-chat");
      updateUrlParams({
        buildTab: "fields-in-chat",
        fieldTab: "fields-in-chat",
      });
    } else if (tab.startsWith("fields")) {
      setActiveBuildTab(tab);
      setActiveFieldTab(tab);
      updateUrlParams({ buildTab: tab, fieldTab: tab });
    } else {
      setActiveBuildTab(tab);
      updateUrlParams({ buildTab: tab });
    }
  };

  const handleFieldTabChange = (tab: string) => {
    setActiveFieldTab(tab);
    updateUrlParams({ fieldTab: tab });
  };

  /* ----------------------- avatar pulse css ----------------------- */

  const avatarPulseStyle = { animation: "avatar-pulse 2s infinite" };

  const ensureAnimationStyles = () => {
    const id = "avatar-pulse-animation";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        @keyframes avatar-pulse {
          0%   { transform: scale(1);   opacity: .7; }
          50%  { transform: scale(1.2); opacity: 0; }
          100% { transform: scale(1);   opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
  };
  ensureAnimationStyles();

  const welcomeMessage = agent.welcome_message || "Hi! How can I assist you?";
  const brandColor = organization?.brand_color || "#060520";

  /* ---------------------- agent editing functions ---------------------- */

  const handleTitleEdit = () => setIsEditingTitle(true);

  const handleTitleSave = async () => {
    const trimmedTitle = title.trim();
    if (trimmedTitle !== agent.title && trimmedTitle !== "") {
      try {
        const success = await updateAgent({ title: trimmedTitle });
        if (!success) {
          setTitle(agent.title || "");
        }
      } catch (error: any) {
        setTitle(agent.title || "");
      }
    } else {
      setTitle(agent.title || "");
    }
    setIsEditingTitle(false);
  };

  const handleNameEdit = () => setIsEditingName(true);

  const handleNameSave = async () => {
    const trimmedName = name.trim();
    if (trimmedName !== agent.agent_name && trimmedName !== "") {
      try {
        const success = await updateAgent({ agent_name: trimmedName });
        if (!success) {
          setName(agent.agent_name || "");
        }
      } catch (error: any) {
        setName(agent.agent_name || "");
      }
    } else {
      setName(agent.agent_name || "");
    }
    setIsEditingName(false);
  };

  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileExt = file.name.split(".").pop();
    const allowedTypes = ["jpg", "jpeg", "png", "gif"];
    if (!allowedTypes.includes(fileExt?.toLowerCase() || "")) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please upload a jpg, jpeg, png, or gif file.",
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: "Please upload a file smaller than 2MB.",
      });
      return;
    }

    setIsUploadingAvatar(true);

    try {
      const fileName = `agent-${agent.id}-${Date.now()}.${fileExt}`;
      const { data, error } = await supabase.storage
        .from("agent_files")
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) throw error;

      const { data: publicUrlData } = supabase.storage
        .from("agent_files")
        .getPublicUrl(fileName);

      const newAvatarUrl = publicUrlData.publicUrl;
      const success = await updateAgent({ avatar_url: newAvatarUrl });

      if (!success) {
        toast({
          variant: "destructive",
          title: "Failed to update avatar",
          description:
            "Avatar was uploaded but failed to save to agent. Please try again.",
        });
        return;
      }

      toast({
        title: "Avatar updated",
        description: "Your agent's avatar has been updated successfully.",
      });
    } catch (error: any) {
      console.error("Storage error:", error);
      toast({
        variant: "destructive",
        title: "Failed to upload avatar",
        description: error.message || "An unexpected error occurred.",
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSelectFromLibrary = async (url: string) => {
    try {
      const success = await updateAgent({ avatar_url: url });
      if (success) {
        toast({
          title: "Avatar updated",
          description: "Your agent's avatar has been updated successfully.",
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Failed to update avatar",
        description: error.message || "An unexpected error occurred.",
      });
    }
  };

  const createNewConversation = async () => {
    if (!id || !user?.id) {
      console.error("Missing agent ID or user ID");
      toast({
        variant: "destructive",
        title: "Error",
        description: "Missing required information to create conversation.",
      });
      return;
    }

    setIsCreatingNewConversation(true);
    try {
      console.log("Creating new conversation for agent:", id, "user:", user.id);

      // Create a new conversation
      const { data, error } = await supabase
        .from("conversations")
        .insert({
          agent_id: id,
          user_id: user.id,
          agent_chat_user_id: null, // Internal conversation
          state: "open",
        })
        .select("id")
        .single();

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      if (!data?.id) {
        throw new Error("No conversation ID returned from database");
      }

      console.log("Created conversation with ID:", data.id);

      // Immediately switch to the new conversation
      setSelectedConversationId(data.id);
      console.log("Set selectedConversationId to:", data.id);

      // Refresh the conversations list
      await refetchInternalConversations();
      console.log("Successfully refreshed conversations list");

      toast({
        title: "New conversation started",
        description: "You can now start chatting with your agent.",
      });
    } catch (error) {
      console.error("Error creating new conversation:", error);
      toast({
        variant: "destructive",
        title: "Failed to create conversation",
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setIsCreatingNewConversation(false);
    }
  };

  /* ---------------------- shared fields section -------------------- */

  const renderFieldsSection = () => (
    <>
      <FieldsTabs
        activeFieldTab={activeFieldTab}
        onTabChange={handleFieldTabChange}
      />
    </>
  );

  /* ---------------------------- render ---------------------------- */

  return (
    <MainLayout>
      <div className="w-full flex flex-1 bg-white min-h-0">
        {/* Left Sidebar - Hidden on mobile */}
        <div
          className={`w-64 bg-[#fafbfc] border-r border-gray-200 flex flex-col ${isMobile ? "hidden" : ""}`}
        >
          <div className="p-4 space-y-6 pt-12">
            {/* Avatar */}
            <div className="flex justify-center">
              <div className="relative group">
                <Avatar className="h-20 w-20">
                  {agent.avatar_url ? (
                    <AvatarImage src={agent.avatar_url} alt={agent.title} />
                  ) : (
                    <AvatarFallback
                      className="text-xl font-semibold"
                      style={{
                        backgroundColor: brandColor,
                        color: ["#fff", "#ffffff"].includes(brandColor)
                          ? "#000"
                          : "#fff",
                      }}
                    >
                      {getInitials(agent.agent_name || agent.title || "A")}
                    </AvatarFallback>
                  )}
                </Avatar>

                {/* Avatar Upload Overlay */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-full transition-all duration-200 flex items-center justify-center">
                      <Edit className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0">
                    <div className="space-y-2 p-4">
                      <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded-md">
                        <Upload className="h-4 w-4" />
                        <span className="text-sm">Upload new image</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          disabled={isUploadingAvatar}
                          className="hidden"
                        />
                      </label>
                      <ImageLibraryPicker
                        onSelectImage={handleSelectFromLibrary}
                        triggerButton={
                          <div className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded-md">
                            <Image className="h-4 w-4" />
                            <span className="text-sm">Choose from library</span>
                          </div>
                        }
                        imageType="avatar"
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Agent Name & Title */}
            <div className="text-center space-y-1">
              {/* Agent Name - Primary (Lexi) */}
              {isEditingName ? (
                <div className="flex items-center space-x-2">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="text-center text-[22px] font-semibold h-9"
                    onBlur={handleNameSave}
                    onKeyDown={(e) => e.key === "Enter" && handleNameSave()}
                    autoFocus
                  />
                  <Button size="sm" variant="ghost" onClick={handleNameSave}>
                    <Check className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="group cursor-pointer" onClick={handleNameEdit}>
                  <h1 className="text-[22px] font-semibold text-gray-900 group-hover:text-gray-700 flex items-center justify-center gap-1">
                    {agent.agent_name || "Agent Name"}
                    <Edit className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h1>
                </div>
              )}

              {/* Title - Secondary (Test knowledge) */}
              {isEditingTitle ? (
                <div className="flex items-center space-x-2">
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="text-center text-base h-8 text-gray-500"
                    onBlur={handleTitleSave}
                    onKeyDown={(e) => e.key === "Enter" && handleTitleSave()}
                    autoFocus
                  />
                  <Button size="sm" variant="ghost" onClick={handleTitleSave}>
                    <Check className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="group cursor-pointer" onClick={handleTitleEdit}>
                  <p className="text-base text-gray-500 group-hover:text-gray-400 flex items-center justify-center gap-1">
                    {agent.title || "Untitled Agent"}
                    <Edit className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </p>
                </div>
              )}
            </div>

            {/* Stats - Smaller */}
            {/* <div className="bg-white rounded-2xl px-5 py-2 shadow-sm border border-gray-100">
              <div className="flex divide-x divide-gray-200">
                <div className="flex-1 text-center pr-3">
                  <p className="text-xs text-gray-500 font-medium">Conversations</p>
                  <p className="text-lg font-semibold mt-0.5 text-gray-900">{stats.total}</p>
                </div>
                <div className="flex-1 text-center pl-3">
                  <p className="text-xs text-gray-500 font-medium">Last 30 days</p>
                  <p className="text-lg font-semibold mt-0.5 text-gray-900">{stats.last30Days}</p>
                </div>
              </div>
            </div> */}

            {/* Build Tabs - Vertical Layout */}
            {activeTab === "build" && (
              <nav className="space-y-1">
                {/* Main Section */}
                <button
                  onClick={() => handleBuildTabChange("behavior")}
                  className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                    activeBuildTab === "behavior"
                      ? "bg-[#8aa3d9] text-white"
                      : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                  }`}
                >
                  <Settings className="h-4 w-4" />
                  Behavior
                </button>
                <button
                  onClick={() => handleBuildTabChange("fields-in-chat")}
                  className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                    activeBuildTab.startsWith("fields")
                      ? "bg-[#8aa3d9] text-white"
                      : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                  }`}
                >
                  <FormInput className="h-4 w-4" />
                  Form Fields
                </button>
                <button
                  onClick={() => handleBuildTabChange("splash-page")}
                  className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                    activeBuildTab === "splash-page"
                      ? "bg-[#8aa3d9] text-white"
                      : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                  }`}
                >
                  <ExternalLink className="h-4 w-4" />
                  Splash page
                </button>
                <button
                  onClick={() => handleBuildTabChange("skills")}
                  className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                    activeBuildTab === "skills"
                      ? "bg-[#8aa3d9] text-white"
                      : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                  }`}
                >
                  <Zap className="h-4 w-4" />
                  Skills
                </button>
                <button
                  onClick={() => handleBuildTabChange("knowledge")}
                  className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                    activeBuildTab === "knowledge"
                      ? "bg-[#8aa3d9] text-white"
                      : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                  }`}
                >
                  <BookOpen className="h-4 w-4" />
                  Knowledge
                </button>

                {/* Advanced Section */}
                <div className="pt-6">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider px-3 mb-3">
                    ADVANCED
                  </h3>
                  <div className="space-y-1">
                    <button
                      onClick={() => handleBuildTabChange("personality")}
                      className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                        activeBuildTab === "personality"
                          ? "bg-[#8aa3d9] text-white"
                          : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                      }`}
                    >
                      <User className="h-4 w-4" />
                      Personality
                    </button>
                    <button
                      onClick={() => handleBuildTabChange("notifications")}
                      className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                        activeBuildTab === "notifications"
                          ? "bg-[#8aa3d9] text-white"
                          : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                      }`}
                    >
                      <BellRing className="h-4 w-4" />
                      Notifications
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleBuildTabChange("admin")}
                        className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                          activeBuildTab === "admin"
                            ? "bg-[#8aa3d9] text-white"
                            : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                        }`}
                      >
                        <Shield className="h-4 w-4" />
                        Admin
                      </button>
                    )}
                  </div>
                </div>
              </nav>
            )}

            {/* Chat History Section - Only show when on chat tab */}
            {activeTab === "chat" && (
              <div className="mt-6 space-y-4">
                {/* New Conversation Button - Claude AI Style */}
                <button
                  onClick={createNewConversation}
                  disabled={isCreatingNewConversation}
                  className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  <div
                    className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                    style={{ backgroundColor: brandColor }}
                  >
                    <Plus className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {isCreatingNewConversation
                      ? "Creating..."
                      : "New conversation"}
                  </span>
                </button>

                {/* History Section */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-3">
                    History
                  </h3>
                  <ScrollArea className="max-h-96">
                    <div className="space-y-1">
                      {internalConversationsLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="text-xs text-gray-500">
                            Loading...
                          </div>
                        </div>
                      ) : internalConversations.length === 0 ? (
                        <div className="text-xs text-gray-400 text-center py-4">
                          No conversations yet
                        </div>
                      ) : (
                        internalConversations.map((conversation) => (
                          <button
                            key={conversation.id}
                            onClick={() => {
                              console.log("=== CONVERSATION CLICK ===");
                              console.log(
                                "Clicking on conversation:",
                                conversation.id,
                              );
                              console.log(
                                "Current selectedConversationId:",
                                selectedConversationId,
                              );
                              console.log(
                                "Conversation preview:",
                                conversation.first_user_message,
                              );
                              setSelectedConversationId(conversation.id);
                              console.log(
                                "Called setSelectedConversationId with:",
                                conversation.id,
                              );
                              console.log("=== END CLICK ===");
                            }}
                            className={`w-full text-left p-2 rounded-md transition-colors text-xs ${
                              selectedConversationId === conversation.id
                                ? "text-white"
                                : "text-gray-600 hover:bg-gray-100"
                            }`}
                            style={
                              selectedConversationId === conversation.id
                                ? {
                                    backgroundColor: brandColor,
                                  }
                                : {}
                            }
                          >
                            <div className="truncate">
                              {conversation.first_user_message.length > 30
                                ? `${conversation.first_user_message.substring(0, 30)}...`
                                : conversation.first_user_message}
                            </div>
                            <div className="text-xs opacity-70 mt-1">
                              {formatDistanceToNow(
                                new Date(conversation.updated_at),
                                { addSuffix: true },
                              )}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col h-full min-h-0">
          {/* Top Navigation with Tabs - Mobile responsive */}
          <div className="border-b border-gray-200 bg-white flex-shrink-0">
            <div
              className={`flex ${isMobile ? "justify-start" : "justify-center"} relative`}
            >
              {/* Mobile controls - Back button and Burger menu */}
              {isMobile && (
                <div className="flex items-center gap-1 px-4 py-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(-1)}
                    className="h-10 w-10 p-0"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMobileSidebarOpen(true)}
                    className="h-10 w-10 p-0"
                  >
                    <AlignJustify className="h-5 w-5" />
                  </Button>
                </div>
              )}

              {/* Scrollable tab container */}
              <div
                className={`flex ${isMobile ? "flex-1 overflow-x-auto scrollbar-hide" : "space-x-8"}`}
              >
                <div
                  className={`flex ${isMobile ? "space-x-3 px-4 pb-0" : "space-x-8"} ${isMobile ? "min-w-max" : ""}`}
                >
                  <button
                    onClick={() => handleTabChange("build")}
                    className={`${isMobile ? "py-3 px-4" : "py-4 px-6"} text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                      activeTab === "build"
                        ? "border-[#8aa3d9] text-[#8aa3d9]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <Settings className="h-4 w-4" />
                    Build
                  </button>
                  <button
                    onClick={() => handleTabChange("chat")}
                    className={`${isMobile ? "py-3 px-4" : "py-4 px-6"} text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                      activeTab === "chat"
                        ? "border-[#8aa3d9] text-[#8aa3d9]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <MessageCircle className="h-4 w-4" />
                    Chat
                  </button>
                  <button
                    onClick={() => handleTabChange("track")}
                    className={`${isMobile ? "py-3 px-4" : "py-4 px-6"} text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                      activeTab === "track"
                        ? "border-[#8aa3d9] text-[#8aa3d9]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <BarChart3 className="h-4 w-4" />
                    Track
                  </button>
                </div>
              </div>

              {/* Action Buttons - Top Right */}
              <div
                className={`${isMobile ? "hidden" : "absolute top-1/2 right-6 transform -translate-y-1/2"} flex items-center gap-2`}
              >
                <TooltipProvider>
                  {/* Copy Agent Link Button */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={async () => {
                          const agentUrl = `${window.location.origin}/${organization?.agent_url}/${agent.public_id}`;
                          try {
                            await navigator.clipboard.writeText(agentUrl);
                            toast({
                              title: "Link copied",
                              description:
                                "Agent link has been copied to clipboard",
                            });
                          } catch (err) {
                            toast({
                              variant: "destructive",
                              title: "Copy failed",
                              description: "Unable to access clipboard",
                            });
                          }
                        }}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg"
                      >
                        <Copy className="h-4 w-4 text-gray-600" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copy agent link</p>
                    </TooltipContent>
                  </Tooltip>

                  {/* Open Public Page Button */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => {
                          const agentUrl = `${window.location.origin}/${organization?.agent_url}/${agent.public_id}`;
                          window.open(agentUrl, "_blank");
                        }}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg"
                      >
                        <ExternalLink className="h-4 w-4 text-gray-600" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Open agent page</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Share Button */}
                <Button
                  onClick={() => setIsPublishModalOpen(true)}
                  className="text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm hover:opacity-90 transition-opacity ml-2"
                  style={{ backgroundColor: "rgb(138, 163, 217)" }}
                >
                  <Share className="h-4 w-4" />
                  Share
                </Button>

                {/* Context Menu with Archive */}
                <div className="ml-2">
                  <AgentContextMenu
                    agent={agent}
                    onArchiveSuccess={() => navigate("/agents")}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Content Area - Scrollable */}
          <div className="flex-1 overflow-auto min-h-0">
            {activeTab === "build" ? (
              <Tabs
                value={activeBuildTab}
                onValueChange={handleBuildTabChange}
                className="w-full flex-1 flex flex-col h-full"
              >
                {/* BEHAVIOR -------------------------------------------------- */}
                <TabsContent value="behavior" className="h-full w-full mt-0">
                  <div className="max-w-[80rem] mx-auto px-4 sm:px-6 py-4 sm:py-8">
                    <AgentBehaviorTab agent={agent} updateAgent={updateAgent} />
                  </div>
                </TabsContent>

                {/* FORM FIELDS: IN-CHAT ------------------------------------ */}
                <TabsContent
                  value="fields-in-chat"
                  className="h-full w-full mt-0"
                >
                  <div className="max-w-[80rem] mx-auto px-4 sm:px-6 py-4 sm:py-8">
                    <div className="space-y-6">
                      {renderFieldsSection()}
                      <AgentFormFieldsTab
                        agent={agent}
                        fieldSection="in_conversation"
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* FORM FIELDS: POST-CHAT ---------------------------------- */}
                <TabsContent
                  value="fields-post-chat"
                  className="h-full w-full mt-0"
                >
                  <div className="max-w-[80rem] mx-auto px-4 sm:px-6 py-4 sm:py-8">
                    <div className="space-y-6">
                      {renderFieldsSection()}
                      <AgentFormFieldsTab
                        agent={agent}
                        fieldSection="post_conversation"
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* SKILLS --------------------------------------------------- */}
                <TabsContent value="skills" className="h-full w-full mt-0">
                  <div className="max-w-[80rem] mx-auto px-4 sm:px-6 py-4 sm:py-8">
                    <AgentSkillsTab agent={agent} updateAgent={updateAgent} />
                  </div>
                </TabsContent>

                {/* SPLASH PAGE ---------------------------------------------- */}
                <TabsContent value="splash-page" className="h-full w-full mt-0">
                  <div className="px-4 sm:px-6 py-4 sm:py-8">
                    <AgentSplashPageTab
                      agent={agent}
                      updateAgent={updateAgent}
                    />
                  </div>
                </TabsContent>

                {/* PERSONALITY ---------------------------------------------- */}
                <TabsContent value="personality" className="h-full w-full mt-0">
                  <div className="max-w-[80rem] mx-auto px-4 sm:px-6 py-4 sm:py-8">
                    <AgentPersonalityTab
                      agent={agent}
                      updateAgent={updateAgent}
                    />
                  </div>
                </TabsContent>

                {/* KNOWLEDGE ------------------------------------------------- */}
                <TabsContent value="knowledge" className="h-full w-full mt-0">
                  <div className="max-w-[80rem] mx-auto px-4 sm:px-6 py-4 sm:py-8">
                    <AgentKnowledgeTab agentId={agent.id} />
                  </div>
                </TabsContent>

                {/* NOTIFICATIONS TAB (formerly post-chat) ------------------ */}
                <TabsContent
                  value="notifications"
                  className="h-full w-full mt-0"
                >
                  <div className="max-w-[80rem] mx-auto px-4 sm:px-6 py-4 sm:py-8">
                    <AgentPostChatTab agent={agent} updateAgent={updateAgent} />
                  </div>
                </TabsContent>

                {/* ADMIN TAB - Only show for super admins ------------------ */}
                {isAdmin && (
                  <TabsContent value="admin" className="h-full w-full mt-0">
                    <div className="max-w-[80rem] mx-auto px-4 sm:px-6 py-4 sm:py-8">
                      <AgentAdminTab agent={agent} updateAgent={updateAgent} />
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            ) : activeTab === "track" ? (
              /* Track Tab Content - Full Width */
              <div className="flex-1 h-full">
                <AgentTrackTab agent={agent} />
              </div>
            ) : (
              /* Chat Tab Content - Full Width */
              <div className="flex-1 h-full">
                <AgentInternalChatTab
                  key={selectedConversationId || "no-conversation"}
                  agent={agent}
                  organization={organization}
                  conversationId={selectedConversationId}
                />
              </div>
            )}
          </div>
        </div>

        {/* Widget is loaded via useEffect above using embed.js script */}

        {/* Publish Modal */}
        <AgentPublishModal
          isOpen={isPublishModalOpen}
          onClose={() => setIsPublishModalOpen(false)}
          agent={agent}
          updateAgent={updateAgent}
        />

        {/* Mobile Sidebar Overlay */}
        {isMobile && (
          <div
            className={`fixed inset-0 z-50 transition-all duration-300 ${
              isMobileSidebarOpen
                ? "opacity-100 visible"
                : "opacity-0 invisible"
            }`}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => setIsMobileSidebarOpen(false)}
            />

            {/* Sidebar Content */}
            <div
              className={`absolute left-0 top-0 h-full w-64 bg-[#fafbfc] transform transition-transform duration-300 ${
                isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
              }`}
            >
              <div className="p-4 space-y-6 pt-12 h-full overflow-y-auto">
                {/* Close button */}
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMobileSidebarOpen(false)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Avatar */}
                <div className="flex justify-center">
                  <Avatar className="h-20 w-20">
                    {agent.avatar_url ? (
                      <AvatarImage src={agent.avatar_url} alt={agent.title} />
                    ) : (
                      <AvatarFallback
                        className="text-xl font-semibold"
                        style={{
                          backgroundColor: brandColor,
                          color: ["#fff", "#ffffff"].includes(brandColor)
                            ? "#000"
                            : "#fff",
                        }}
                      >
                        {getInitials(agent.agent_name || agent.title || "A")}
                      </AvatarFallback>
                    )}
                  </Avatar>
                </div>

                {/* Agent Name & Title */}
                <div className="text-center space-y-1">
                  <h1 className="text-[22px] font-semibold text-gray-900">
                    {agent.agent_name || "Agent Name"}
                  </h1>
                  <p className="text-base text-gray-500">
                    {agent.title || "Untitled Agent"}
                  </p>
                </div>

                {/* Build Tabs - Only show if on build tab */}
                {activeTab === "build" && (
                  <nav className="space-y-1">
                    {/* Main Section */}
                    <button
                      onClick={() => {
                        handleBuildTabChange("behavior");
                        setIsMobileSidebarOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                        activeBuildTab === "behavior"
                          ? "bg-[#8aa3d9] text-white"
                          : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                      }`}
                    >
                      <Settings className="h-4 w-4" />
                      Behavior
                    </button>
                    <button
                      onClick={() => {
                        handleBuildTabChange("fields-in-chat");
                        setIsMobileSidebarOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                        activeBuildTab.startsWith("fields")
                          ? "bg-[#8aa3d9] text-white"
                          : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                      }`}
                    >
                      <FormInput className="h-4 w-4" />
                      Form Fields
                    </button>
                    <button
                      onClick={() => {
                        handleBuildTabChange("splash-page");
                        setIsMobileSidebarOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                        activeBuildTab === "splash-page"
                          ? "bg-[#8aa3d9] text-white"
                          : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                      }`}
                    >
                      <ExternalLink className="h-4 w-4" />
                      Splash page
                    </button>
                    <button
                      onClick={() => {
                        handleBuildTabChange("skills");
                        setIsMobileSidebarOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                        activeBuildTab === "skills"
                          ? "bg-[#8aa3d9] text-white"
                          : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                      }`}
                    >
                      <Zap className="h-4 w-4" />
                      Skills
                    </button>
                    <button
                      onClick={() => {
                        handleBuildTabChange("knowledge");
                        setIsMobileSidebarOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                        activeBuildTab === "knowledge"
                          ? "bg-[#8aa3d9] text-white"
                          : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                      }`}
                    >
                      <BookOpen className="h-4 w-4" />
                      Knowledge
                    </button>

                    {/* Advanced Section */}
                    <div className="pt-6">
                      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider px-3 mb-3">
                        ADVANCED
                      </h3>
                      <div className="space-y-1">
                        <button
                          onClick={() => {
                            handleBuildTabChange("personality");
                            setIsMobileSidebarOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                            activeBuildTab === "personality"
                              ? "bg-[#8aa3d9] text-white"
                              : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                          }`}
                        >
                          <User className="h-4 w-4" />
                          Personality
                        </button>
                        <button
                          onClick={() => {
                            handleBuildTabChange("notifications");
                            setIsMobileSidebarOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                            activeBuildTab === "notifications"
                              ? "bg-[#8aa3d9] text-white"
                              : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                          }`}
                        >
                          <BellRing className="h-4 w-4" />
                          Notifications
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => {
                              handleBuildTabChange("admin");
                              setIsMobileSidebarOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                              activeBuildTab === "admin"
                                ? "bg-[#8aa3d9] text-white"
                                : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                            }`}
                          >
                            <Shield className="h-4 w-4" />
                            Admin
                          </button>
                        )}
                      </div>
                    </div>
                  </nav>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AgentDetail;
